import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { url } from "../../Constant/TextConstant";

const ForgotPasswordScreen = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPhoneNumberVerified, setIsPhoneNumberVerified] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState("");
  const [receivedOtp, setReceivedOtp] = useState("");
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const baseAPIUrl = url.baseAPIUrl;

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  useEffect(() => {
    let timer;
    if (isOtpSent && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown, isOtpSent]);

  const verifyPhoneNumber = async () => {
    if (!phoneNumber) {
      setErrorMessage("Please enter phone number");
      return;
    }

    if (!/^\d{10}$/.test(phoneNumber)) {
      setErrorMessage("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.get(
        `${baseAPIUrl}/api/LoginMaster/GetForgotOtp?mobile=${phoneNumber}`
      );

      if (response.data.Message === "OTP sent successfully.") {
        setReceivedOtp(response.data.OTP);
        setIsPhoneNumberVerified(true);
        setIsOtpSent(true);
        startCountdown();
        toast.success("OTP sent to your mobile number");
      } else {
        setErrorMessage("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setErrorMessage("This phone number is not registered");
      } else {
        setErrorMessage("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = () => {
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }

    if (otp !== receivedOtp) {
      toast.error("The OTP you entered is incorrect");
      return;
    }

    setIsOtpVerified(true);
    toast.success("Mobile number verified successfully!");
  };

  const resetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setErrorMessage("Please fill in all fields");
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${baseAPIUrl}/api/LoginMaster/resetpassword`,
        {
          NewPassword: newPassword,
          Mobile: phoneNumber,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success(
          "Password reset successfully! You can now login with your new password"
        );
        // Reset form
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      setErrorMessage("Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const startCountdown = () => {
    setCountdown(30);
  };

  const resendOtp = () => {
    verifyPhoneNumber();
    startCountdown();
  };

  return (
    <Container>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <AppBar>
        <AppBarTitle>Forgot Password</AppBarTitle>
      </AppBar>

      <Content>
        <Card>
          <Title>Reset Your Password</Title>
          <Subtitle>Enter your details to reset your password</Subtitle>

          {!isPhoneNumberVerified ? (
            <>
              <InputContainer>
                <Label>Mobile Number</Label>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) =>
                    setPhoneNumber(
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                  placeholder="Enter 10-digit mobile number"
                  inputMode="numeric"
                />
              </InputContainer>

              <Button onClick={verifyPhoneNumber} disabled={isLoading}>
                {isLoading ? "Sending..." : "Send OTP"}
              </Button>

              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </>
          ) : !isOtpVerified ? (
            <>
              <InputContainer>
                <Label>Enter OTP</Label>
                <Input
                  type="number"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  inputMode="numeric"
                />
                <OTPHint>We've sent an OTP to your mobile number</OTPHint>
              </InputContainer>

              <Button onClick={verifyOtp}>Verify OTP</Button>

              {isOtpSent && (
                <OtpResendContainer>
                  {countdown > 0 ? (
                    <OtpTimer>Resend OTP in {countdown} seconds</OtpTimer>
                  ) : (
                    <ResendButton onClick={resendOtp}>Resend OTP</ResendButton>
                  )}
                </OtpResendContainer>
              )}
            </>
          ) : (
            <>
              <InputContainer>
                <Label>New Password</Label>
                <PasswordInputContainer>
                  <Input
                    type={isNewPasswordVisible ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                  />
                  <ToggleVisibilityButton
                    onClick={() =>
                      setIsNewPasswordVisible(!isNewPasswordVisible)
                    }
                    type="button"
                  >
                    {isNewPasswordVisible ? (
                      <i className="fas fa-eye-slash"></i>
                    ) : (
                      <i className="fas fa-eye"></i>
                    )}
                  </ToggleVisibilityButton>
                </PasswordInputContainer>
              </InputContainer>

              <InputContainer>
                <Label>Confirm New Password</Label>
                <PasswordInputContainer>
                  <Input
                    type={isConfirmPasswordVisible ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                  />
                  <ToggleVisibilityButton
                    onClick={() =>
                      setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
                    }
                    type="button"
                  >
                    {isConfirmPasswordVisible ? (
                      <i className="fas fa-eye-slash"></i>
                    ) : (
                      <i className="fas fa-eye"></i>
                    )}
                  </ToggleVisibilityButton>
                </PasswordInputContainer>
              </InputContainer>

              <Button onClick={resetPassword} disabled={isLoading}>
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>

              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </>
          )}
        </Card>
      </Content>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f5f7fa;
`;

const AppBar = styled.div`
  background-color: #2c3e50;
  color: white;
  padding: 18px 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const AppBarTitle = styled.h1`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;

  @media (max-width: 576px) {
    padding: 1.75rem;
    box-shadow: none;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const InputContainer = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #34495e;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1px solid #dfe6e9;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background-color: #f8f9fa;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
    background-color: white;
  }

  &::placeholder {
    color: #bdc3c7;
  }
`;

const OTPHint = styled.p`
  font-size: 0.8rem;
  color: #7f8c8d;
  margin-top: 0.5rem;
`;

const PasswordInputContainer = styled.div`
  position: relative;
`;

const ToggleVisibilityButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #7f8c8d;
  font-size: 1rem;
  padding: 0.5rem;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: #f1f1f1;
    color: #34495e;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.85rem;
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #1a252f;
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
  padding: 0.5rem;
  border-radius: 4px;
  background-color: rgba(231, 76, 60, 0.1);
`;

const OtpResendContainer = styled.div`
  margin-top: 1.5rem;
  text-align: center;
`;

const OtpTimer = styled.p`
  color: #7f8c8d;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
`;

const ResendButton = styled.button`
  background: transparent;
  color: #3498db;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    color: #2980b9;
    background-color: rgba(52, 152, 219, 0.1);
  }
`;

export default ForgotPasswordScreen;
